const fontFamily = [
  '"Inter Variable"',
  '"Inter"',
  '-apple-system',
  'BlinkMacSystemFont',
  '"Segoe UI"',
  'Roboto',
  '"Helvetica Neue"',
  'Arial',
  'sans-serif',
  '"Apple Color Emoji"',
  '"Segoe UI Emoji"',
  '"Segoe UI Symbol"',
].join(',')

const weights = {
  regular: 400,
  medium: 500,
  semiBold: 600,
  bold: 700,
} as const

const styles = {
  'futureTheme.display-h1': {
    fontWeight: weights.bold,
    fontSize: '2rem',
    lineHeight: '2.75rem',
    letterSpacing: '-0.45px',
  },
  'futureTheme.display-h2': {
    fontWeight: weights.bold,
    fontSize: '1.5rem',
    lineHeight: '2.375rem',
    letterSpacing: '-0.45px',
  },
  'futureTheme.display-h3': {
    fontWeight: weights.semiBold,
    fontSize: '1.25rem',
    lineHeight: '2rem',
    letterSpacing: '0',
  },
  'futureTheme.display-h4': {
    fontWeight: weights.semiBold,
    fontSize: '1rem',
    lineHeight: '1.5rem',
    letterSpacing: '0',
  },
  'futureTheme.upsized-regular': {
    fontWeight: weights.regular,
    fontSize: '1rem',
    lineHeight: '1.75rem',
    letterSpacing: '0',
  },
  'futureTheme.upsized-medium': {
    fontWeight: weights.medium,
    fontSize: '1rem',
    lineHeight: '1.75rem',
    letterSpacing: '0',
  },
  'futureTheme.upsized-semiBold': {
    fontWeight: weights.semiBold,
    fontSize: '1rem',
    lineHeight: '1.75rem',
    letterSpacing: '0',
  },
  'futureTheme.upsized-bold': {
    fontWeight: weights.bold,
    fontSize: '1rem',
    lineHeight: '1.75rem',
    letterSpacing: '0',
  },
  'futureTheme.body-regular': {
    fontWeight: weights.regular,
    fontSize: '0.875rem',
    lineHeight: '1.5rem',
    letterSpacing: '0',
  },
  'futureTheme.body-medium': {
    fontWeight: weights.medium,
    fontSize: '0.875rem',
    lineHeight: '1.5rem',
    letterSpacing: '0',
  },
  'futureTheme.body-semiBold': {
    fontWeight: weights.semiBold,
    fontSize: '0.875rem',
    lineHeight: '1.5rem',
    letterSpacing: '0',
  },
  'futureTheme.body-bold': {
    fontWeight: weights.bold,
    fontSize: '0.875rem',
    lineHeight: '1.5rem',
    letterSpacing: '0',
  },
  'futureTheme.small-regular': {
    fontWeight: weights.regular,
    fontSize: '0.75rem',
    lineHeight: '1.125rem',
    letterSpacing: '0',
  },
  'futureTheme.small-medium': {
    fontWeight: weights.medium,
    fontSize: '0.75rem',
    lineHeight: '1.125rem',
    letterSpacing: '0',
  },
  'futureTheme.small-bold': {
    fontWeight: weights.semiBold,
    fontSize: '0.75rem',
    lineHeight: '1.125rem',
    letterSpacing: '0',
  },
  'futureTheme.small-uppercase': {
    fontWeight: weights.medium,
    fontSize: '0.625rem',
    lineHeight: '1rem',
    letterSpacing: '1px',
    textTransform: 'uppercase',
  },
  'futureTheme.extraSmall-semiBold': {
    fontWeight: weights.semiBold,
    fontSize: '0.5625rem',
    lineHeight: '1rem',
    letterSpacing: '0.45px',
  },
  'futureTheme.extraSmall-medium': {
    fontWeight: weights.medium,
    fontSize: '0.5625rem',
    lineHeight: '1rem',
    letterSpacing: '0.45px',
  },
} as const

// Theme1 typography variants mapped onto theme2 equivalents. See
// `../typography-migration.md` for usage counts and the rationale behind each
// mapping. Goal: every theme1 variant resolves to *something* so call sites
// can keep compiling while the migration runs.
const legacyStyles = {
  // ─── Exact matches (same size & weight) ────────────────────────────────
  subtitleLargeMedium: styles['futureTheme.upsized-medium'],
  Body12pxRegular: styles['futureTheme.small-regular'],
  Body12pxMedium: styles['futureTheme.small-medium'],
  Body14pxRegular: styles['futureTheme.body-regular'],
  Body14pxMedium: styles['futureTheme.body-medium'],
  Body14pxHeavy: styles['futureTheme.body-bold'],
  Body16pxRegular: styles['futureTheme.upsized-regular'],
  Body16pxMedium: styles['futureTheme.upsized-medium'],
  Body16pxHeavy: styles['futureTheme.upsized-bold'],
  HeaderSubtitleSmallRegular: styles['futureTheme.small-regular'],
  HeaderSubtitleSmallMedium: styles['futureTheme.small-medium'],
  // theme2's `small-bold` is actually weight 600 (semi-bold) — matches theme1.
  HeaderSubtitleSmallSemibold: styles['futureTheme.small-bold'],
  HeaderSubtitleMediumRegular: styles['futureTheme.body-regular'],
  HeaderSubtitleMediumMedium: styles['futureTheme.body-medium'],
  HeaderSubtitleMediumSemibold: styles['futureTheme.body-semiBold'],
  HeaderSubtitleMediumBold: styles['futureTheme.body-bold'],
  HeaderSubtitleLargeRegular: styles['futureTheme.upsized-regular'],
  HeaderSubtitleLargeMedium: styles['futureTheme.upsized-medium'],
  HeaderSubtitleLargeSemibold: styles['futureTheme.upsized-semiBold'],
  HelperText: styles['futureTheme.small-regular'],

  // ─── Close matches (accept a size or weight gap) ───────────────────────
  // theme1 was 18/600 → display-h3 is 20/600. Gap: +2px size.
  H6Semibold: styles['futureTheme.display-h3'],
  // theme1 was 20/500 → display-h3 is 20/600. Gap: +100 weight (medium → semi-bold).
  H6Medium: styles['futureTheme.display-h3'],
  // theme1 was 25/600 → display-h2 is 24/700. Gap: -1px size, +100 weight.
  H5Semibold: styles['futureTheme.display-h2'],
  // theme1 was 31/600 → display-h1 is 32/700. Gap: +1px size, +100 weight.
  H4Semibold: styles['futureTheme.display-h1'],
  // theme1 was 12/700 → small-bold is 12/600. Gap: -100 weight.
  Body12pxHeavy: styles['futureTheme.small-bold'],

  // ─── No good match (preserve original via override) ────────────────────
  // theme2 has no 10px tier — recreate from small-regular (12/400).
  Body10pxHeavy: {
    ...styles['futureTheme.small-regular'],
    fontSize: '0.625rem',
    fontWeight: weights.bold,
  },
  HeaderSubtitleXSmallRegular: {
    ...styles['futureTheme.small-regular'],
    fontSize: '0.625rem',
  },
  Tooltip: {
    ...styles['futureTheme.small-regular'],
    fontSize: '0.625rem',
  },
  // theme2 has no 18px tier — recreate from the 16px upsized family.
  H6Regular: {
    ...styles['futureTheme.upsized-regular'],
    fontSize: '1.125rem',
  },
  Body18pxRegular: {
    ...styles['futureTheme.upsized-regular'],
    fontSize: '1.125rem',
  },
  Body18pxMedium: {
    ...styles['futureTheme.upsized-medium'],
    fontSize: '1.125rem',
  },
  // theme2 has no 300 weight — recreate from body-regular.
  HeaderSubtitleMediumLight: {
    ...styles['futureTheme.body-regular'],
    fontWeight: 300,
  },
  // theme2 has no 24–25px regular/medium — recreate from display-h2 (24/700).
  H5Regular: {
    ...styles['futureTheme.display-h2'],
    fontSize: '1.5625rem',
    fontWeight: weights.regular,
  },
  H5Medium: {
    ...styles['futureTheme.display-h2'],
    fontSize: '1.5625rem',
    fontWeight: weights.medium,
  },
  // theme1 was 31/500 → display-h1 at medium weight. Gap: +1px size.
  H4Medium: {
    ...styles['futureTheme.display-h1'],
    fontWeight: weights.medium,
  },
  // Bespoke marketing — theme2 has no 36px display.
  Tagline: {
    ...styles['futureTheme.display-h1'],
    fontSize: '2.25rem',
    fontWeight: weights.semiBold,
    lineHeight: '43px',
  },
} as const

// ─── MUI built-in variants ───────────────────────────────────────────────
// Spread the closest theme2 style and override only the fontSize to the *old
// theme*'s size (not MUI's upstream defaults), so call sites still using
// built-in variants render at the size they always have while inheriting
// theme2's weight/line-height. Some are read directly (GenericAuditLogTable
// reads body1, MUI DataGrid reads caption), so none are left undefined.
const builtinStyles = {
  h1: { ...styles['futureTheme.display-h1'], fontSize: 32 },
  h2: { ...styles['futureTheme.display-h1'], fontSize: 32 },
  h3: { ...styles['futureTheme.display-h2'], fontSize: 22 },
  h4: { ...styles['futureTheme.display-h3'], fontSize: 18 },
  h5: { ...styles['futureTheme.display-h4'], fontSize: 16 },
  h6: { ...styles['futureTheme.body-semiBold'], fontSize: 14 },
  subtitle1: { ...styles['futureTheme.upsized-regular'], fontSize: 16 },
  subtitle2: { ...styles['futureTheme.body-medium'], fontSize: 14 },
  body1: { ...styles['futureTheme.body-regular'], fontSize: 14 },
  body2: { ...styles['futureTheme.body-regular'], fontSize: 14 },
  caption: { ...styles['futureTheme.small-regular'], fontSize: 12 },
  // Not customised by the old theme — closest theme2 style at the MUI default size.
  button: { ...styles['futureTheme.body-medium'], fontSize: 14 },
  overline: { ...styles['futureTheme.small-regular'], fontSize: 12 },
}

export const typography = {
  fontFamily,
  weights,
  ...styles,
  ...legacyStyles,
  ...builtinStyles,
} as const

export const MuiTypography = {
  defaultProps: {
    variantMapping: {
      'futureTheme.display-h1': 'h1',
      'futureTheme.display-h2': 'h2',
      'futureTheme.display-h3': 'h3',
      'futureTheme.display-h4': 'h4',
    },
  },
}
